
import { getRenewal, renewalResponse, rnw02p, insuranceFeeOptions } from "@/api/renewal";
import { renewalSubmitRequest } from "@/api/renewalSubmit";
import router from "@/router";
import axios from "axios";
import { commaSplit } from "common/src/utils/number";
import { getGenderText, getHaraikomiText } from "common/src/utils/codes";
import HyphenParagraph from "common/src/components/HyphenParagraph.vue";
import constants from "common/src/constants";
import { defineComponent, reactive, Ref, ref, UnwrapRef } from "vue";
import SubInsured from "common/src/components/SubInsured.vue";
import useVuelidate from "@vuelidate/core";
import { SCR130FormValidateRule } from "@/forms/scr130";
import ErrorField from "common/src/components/ErrorField.vue";
import { scrollToError } from "common/src/utils/screen";
import { MutationTypes, useStore } from "@/store";
import { getSyoyuText, getYoutoText, getKozoText } from "common/src/utils/codes";

export default defineComponent({
  components: {
    HyphenParagraph,
    SubInsured,
    ErrorField,
  },
  setup() {
    const renewal: UnwrapRef<renewalResponse> = reactive({
      rnw01p: undefined,
      hsm03p: undefined,
      rnw02p: undefined,
    });
    const mainInsured: Ref<rnw02p | undefined> = ref(undefined);
    const subInsureds: Ref<rnw02p[] | undefined> = ref(undefined);

    const feeOptions: Ref<insuranceFeeOptions[]> = ref([]);
    const houseOptions: Ref<insuranceFeeOptions[]> = ref([]);
    const liabilityOptions: Ref<insuranceFeeOptions[]> = ref([]);
    const nowDate = `${new Date().getFullYear()}/${`00${new Date().getMonth() + 1}`.slice(-2)}/${`00${new Date().getDate()}`.slice(-2)}`;

    const form: UnwrapRef<renewalSubmitRequest> = reactive({
      rnw01p: {
        tfRekytl: "",
        tfRekyt2: "",
        tfRehscd: "",
        tfRehkpl: "",
        tfRegkhk: 0,
        tfRehkhh: "",
        tfReconv: "",
        tfRethum: "",
        tfRethcm: "",
        tfRethsr: "",
        tfRehkdt: 0,
      },
      rnw02p: {
        tfRhhktl: "",
        tfRhhkt2: "",
        tfRhhkdt: 0,
      },
      security: {
        tfHnuuid: "",
      },
    });

    // 他保険種類管理配列
    const thRethsrArr: Ref<string[]> = ref([]);

    const store = useStore();

    getRenewal()
      .then((res) => {
        if (res.data.rnw01p?.tf_rekzis === constants.KEIZOKU_ISHI.ARI) {
          router.push({ name: "404" });
          return;
        }

        renewal.rnw01p = res.data.rnw01p;
        renewal.hsm03p = res.data.hsm03p;
        renewal.rnw02p = res.data.rnw02p;

        mainInsured.value = res.data.rnw02p?.find((i) => i.tf_rhhkno === constants.MAIN_INSURED_NUMBER);
        subInsureds.value = res.data.rnw02p
          ?.filter((i) => i.tf_rhhkno !== constants.MAIN_INSURED_NUMBER)
          .sort((a, b) => Number.parseInt(a.tf_rhhkno) - Number.parseInt(b.tf_rhhkno));

        // 更新契約手続きAPI呼び出し用Formに値をセット
        // ストアにFormの値がセットされている場合、ストアの値をセット
        const movedFromConfOrPay = store.state.renewal?.rnw01p.tfRekytl !== undefined;
        form.rnw01p.tfRekytl = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRekytl : res.data.rnw01p?.tf_rekytl;
        form.rnw01p.tfRekyt2 = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRekyt2 : res.data.rnw01p?.tf_rekyt2;
        form.rnw01p.tfRehscd = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRehscd : res.data.rnw01p?.tf_rehscd;
        form.rnw01p.tfRehkpl = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRehkpl : res.data.rnw01p?.tf_rehkpl;
        form.rnw01p.tfRegkhk = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRegkhk : res.data.rnw01p?.tf_regkhk;
        form.rnw01p.tfRehkhh = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRehkhh : constants.HARAIKOMI_HOUHOU.CONVENI;
        form.rnw01p.tfRehkdt = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRehkdt : res.data.rnw01p?.tf_rehkdt;
        form.rnw01p.tfRethum = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRethum : constants.UMU.NASI;
        form.rnw01p.tfRethcm = movedFromConfOrPay ? store.state.renewal?.rnw01p?.tfRethcm : "";
        if (movedFromConfOrPay && store.state.renewal?.rnw01p.tfRethsr !== undefined && store.state.renewal?.rnw01p.tfRethsr.length > 0) {
          thRethsrArr.value.push(...(store.state.renewal?.rnw01p.tfRethsr?.split("/") ?? []));
        }
        form.rnw02p.tfRhhktl = movedFromConfOrPay ? store.state.renewal?.rnw02p?.tfRhhktl : mainInsured.value?.tf_rhhktl;
        form.rnw02p.tfRhhkt2 = movedFromConfOrPay ? store.state.renewal?.rnw02p?.tfRhhkt2 : mainInsured.value?.tf_rhhkt2;
        form.rnw02p.tfRhhkdt = movedFromConfOrPay ? store.state.renewal?.rnw02p?.tfRhhkdt : mainInsured.value?.tf_rhhkdt;
        form.security.tfHnuuid = store.state.renewal?.security?.tfHnuuid;

        if (renewal.rnw01p && form.security.tfHnuuid && store.state.mail) {
          renewal.rnw01p.tf_rekyma = store.state.mail;
        }

        // プラン部分のoption構築
        res.data.hsm03p?.forEach((p) => {
          feeOptions.value.push({ plan: p.tf_hrplan, text: `${commaSplit(p.tf_hrhrgk)}円` });
          houseOptions.value.push({
            plan: p.tf_hrplan,
            text: `保険金額：${commaSplit(p.tf_hrhkkg)}万円 | 保険料：${commaSplit(p.tf_hrhrhk)}円`,
          });
          liabilityOptions.value.push({
            plan: p.tf_hrplan,
            text: `保険金額：${commaSplit(p.tf_hrhtkg)}万円 | 保険料：${commaSplit(p.tf_hrhthk)}円`,
          });
        });
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          router.push({ name: "404" });
        }
      });

    // フィールドエラーを外部からセットする変数
    const $externalResults = ref({
      rnw01p: {},
      rnw02p: {},
    });
    // フィールドエラー設定初期化
    const f$ = useVuelidate(SCR130FormValidateRule, form, {
      $autoDirty: true,
      $externalResults,
    });

    // 電話番号入力時の検証処理
    const validTelNo = () => {
      $externalResults.value.rnw01p = {
        ...$externalResults.value.rnw01p,
        ...{ tfRekytl: [], tfRekyt2: [] },
      };
      f$.value.rnw01p.tfRekytl.$touch();
      f$.value.rnw01p.tfRekyt2.$touch();
      if (form.rnw01p.tfRekytl === "" && form.rnw01p.tfRekyt2 === "") {
        $externalResults.value.rnw01p = {
          ...$externalResults.value.rnw01p,
          ...{ tfRekytl: ["いずれかの電話番号を入力してください"], tfRekyt2: ["いずれかの電話番号を入力してください"] },
        };
      }
    };

    // 被保険者電話番号入力時の検証処理
    const validInsuredTelNo = () => {
      $externalResults.value.rnw02p = {
        ...$externalResults.value.rnw02p,
        ...{ tfRhhktl: [], tfRhhkt2: [] },
      };
      f$.value.rnw02p.tfRhhktl.$touch();
      f$.value.rnw02p.tfRhhkt2.$touch();
      if (form.rnw02p.tfRhhktl === "" && form.rnw02p.tfRhhkt2 === "") {
        $externalResults.value.rnw02p = {
          ...$externalResults.value.rnw02p,
          ...{ tfRhhktl: ["いずれかの電話番号を入力してください"], tfRhhkt2: ["いずれかの電話番号を入力してください"] },
        };
      }
    };

    // 他保険会社名に関する検証処理
    const validOtherInsurance = () => {
      $externalResults.value.rnw01p = {
        ...$externalResults.value.rnw01p,
        ...{ tfRethcm: [] },
      };
      f$.value.rnw01p.tfRethcm.$touch();
      if (form.rnw01p.tfRethum === constants.UMU.ARI) {
        if (form.rnw01p.tfRethcm === "") {
          $externalResults.value.rnw01p = {
            ...$externalResults.value.rnw01p,
            ...{ tfRethcm: ["会社名を入力してください"] },
          };
        }

        if ((form.rnw01p.tfRethcm?.length ?? 0) > 120) {
          $externalResults.value.rnw01p = {
            ...$externalResults.value.rnw01p,
            ...{ tfRethcm: ["会社名は120文字以内で入力してください"] },
          };
        }
      }
    };

    // 検証後、更新契約手続きFormをstoreに格納
    const submit = async () => {
      f$.value.$touch();
      validInsuredTelNo();
      validTelNo();
      validOtherInsurance();
      if (f$.value.$invalid) {
        scrollToError();
        return;
      }

      // 契約者と同じの場合は、被保険者へ契約者情報をセット
      if (mainInsured.value?.tf_rhkysm === constants.HAIIIE.HAI) {
        form.rnw02p.tfRhhktl = form.rnw01p.tfRekytl;
        form.rnw02p.tfRhhkt2 = form.rnw01p.tfRekyt2;
      }

      // 他保険なしの場合は、他保険会社名・他保険種類をセットしない
      if (form.rnw01p.tfRethum === constants.UMU.NASI) {
        form.rnw01p.tfRethcm = "";
        form.rnw01p.tfRethsr = "";
      } else {
        form.rnw01p.tfRethsr = thRethsrArr.value.join("/");
      }

      // 保険プランから保険商品コードと保険料を事前に退避させておく
      form.rnw01p.tfRehscd = renewal.hsm03p?.find((p) => p.tf_hrplan === form.rnw01p.tfRehkpl)?.tf_hrshcd;
      form.rnw01p.tfRegkhk = renewal.hsm03p?.find((p) => p.tf_hrplan === form.rnw01p.tfRehkpl)?.tf_hrhrgk;

      store.commit(MutationTypes.SET_RENEWAL, form);

      router.push({ name: "RenewalConfirm" });
    };

    return {
      renewal,
      thRethsrArr,
      mainInsured,
      subInsureds,
      feeOptions,
      houseOptions,
      liabilityOptions,
      nowDate,
      constants,
      form,
      getGenderText,
      getHaraikomiText,
      SubInsured,
      commaSplit,
      validTelNo,
      validInsuredTelNo,
      validOtherInsurance,
      f$,
      submit,
      getSyoyuText,
      getYoutoText,
      getKozoText,
    };
  },
});
